import { type VoicePart, VOICE_PARTS } from '@corocanto/scoring';
import type { PlaybackMode } from '../audio/mix';
import type { PartFocus } from '../audio/rehearsal';

/**
 * Icons for the mixer presets of the practice and rehearsal pages, drawn as one family: four
 * figures for the four parts, S A T B from left to right as on the score, and a keyboard for the
 * accompaniment.
 */

function Person({ x, scale = 1, faint = false }: { x: number; scale?: number; faint?: boolean }) {
  const r = 2.5 * scale;
  const w = 4.3 * scale;
  return (
    <g opacity={faint ? 0.4 : 1}>
      <circle cx={x} cy={13 - 5.2 * scale} r={r} />
      <path d={`M ${x - w} 20 c 0 -3.6 ${w * 0.45} -5.6 ${w} -5.6 s ${w} 2 ${w} 5.6`} />
    </g>
  );
}

function Keyboard() {
  return (
    <g>
      <rect x={4} y={5} width={22} height={15} rx={1.5} />
      {[8.4, 12.8, 17.2, 21.6].map((x) => (
        <line key={x} x1={x} y1={12.5} x2={x} y2={20} />
      ))}
      {[8.4, 12.8, 21.6].map((x) => (
        <rect key={x} x={x - 1.4} y={5} width={2.8} height={7.5} fill="currentColor" stroke="none" />
      ))}
    </g>
  );
}

/** How a part's figure looks: in front (large, bold), normal, further back (small, faint) or off (an empty spot). */
type Look = 'front' | 'normal' | 'back' | 'off';

/** Where each voice part stands: S, A, T, B from left to right, as on the score. */
const FIGURE_X: Record<VoicePart, number> = { S: 4.5, A: 14.2, T: 23.8, B: 33.5 };

function PartsIcon({ look }: { look: (part: VoicePart) => Look }) {
  return (
    <svg
      width={46}
      height={29}
      viewBox="0 0 38 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {VOICE_PARTS.map((p) => {
        const x = FIGURE_X[p];
        switch (look(p)) {
          case 'front':
            return (
              <g key={p} strokeWidth={2.3}>
                <Person x={x} scale={1.1} />
              </g>
            );
          case 'normal':
            return <Person key={p} x={x} scale={0.8} />;
          case 'back':
            return <Person key={p} x={x} scale={0.7} faint />;
          case 'off':
            return <line key={p} x1={x - 2.6} x2={x + 2.6} y1={20} y2={20} opacity={0.45} />;
        }
      })}
    </svg>
  );
}

/**
 * The practice presets, drawn for the chorist's own part: "only my part" shows it alone, "all,
 * mine louder" in front, "all except mine" missing. Without a part, only "all" and the
 * accompaniment are offered.
 */
export function ModeIcon({ mode, myPart = null }: { mode: PlaybackMode; myPart?: VoicePart | null }) {
  if (mode === 'accompaniment') {
    return (
      <svg
        width={36}
        height={29}
        viewBox="0 0 30 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        <Keyboard />
      </svg>
    );
  }
  const look = (p: VoicePart): Look => {
    if (mode === 'all' || !myPart) return 'normal';
    const mine = p === myPart;
    if (mode === 'mine') return mine ? 'front' : 'off';
    if (mode === 'mineLouder') return mine ? 'front' : 'back';
    return mine ? 'off' : 'normal';
  };
  return <PartsIcon look={look} />;
}

/** The rehearsal presets: all four parts alike, or one part in front with the others further back. */
export function FocusIcon({ focus }: { focus: PartFocus }) {
  return <PartsIcon look={(p) => (focus === 'all' ? 'normal' : p === focus ? 'front' : 'back')} />;
}
